import { useMemo, useState } from "react";
import { CalendarPlus } from "lucide-react";
import { Input } from "../../components/ui/input";
import { cn } from "../../lib/utils";

export function toISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function todayISO() {
  return toISO(new Date());
}

// Bugünden (ya da bölgenin en erken teslimat tarihinden) başlayarak "count"
// günlük tıklanabilir tarih seçeneği üretir — "11 Temmuz, 12 Temmuz..." şeklinde.
function buildDateOptions(minDateISO, count) {
  const start = minDateISO ? new Date(minDateISO + "T00:00:00") : new Date();
  const today = todayISO();
  const tomorrow = toISO(new Date(Date.now() + 86400000));
  const days = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    const iso = toISO(d);
    days.push({
      iso,
      day: d.toLocaleDateString("tr-TR", { day: "numeric" }),
      month: d.toLocaleDateString("tr-TR", { month: "short" }),
      badge: iso === today ? "Bugün" : iso === tomorrow ? "Yarın" : null,
    });
  }
  return days;
}

// Bugünden itibaren 4 hızlı seçenek gösterir; daha ileri bir tarih için
// "Diğer Tarih" kutusuna tıklanınca serbest tarih seçici açılır.
export function DateChipPicker({ minDateISO, value, onChange }) {
  const quickOptions = useMemo(() => buildDateOptions(minDateISO, 4), [minDateISO]);
  const isQuickSelected = quickOptions.some((d) => d.iso === value);
  const [customOpen, setCustomOpen] = useState(false);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2" data-testid="checkout-date-picker">
        {quickOptions.map((d) => (
          <button
            key={d.iso}
            type="button"
            onClick={() => { onChange(d.iso); setCustomOpen(false); }}
            className={cn(
              "flex-1 flex flex-col items-center justify-center gap-0.5 py-3 rounded-xl border text-center transition-colors",
              value === d.iso
                ? "bg-[#A85C34] border-[#A85C34] text-white"
                : "bg-white border-[#EAEAEA] text-[#222222] hover:border-[#A85C34]"
            )}
            data-testid={`checkout-date-option-${d.iso}`}
          >
            {d.badge ? (
              <span className="text-[10px] font-semibold uppercase tracking-wide">{d.badge}</span>
            ) : (
              <span className="text-[10px] uppercase tracking-wide opacity-70">{d.month}</span>
            )}
            <span className="text-base font-serif font-bold leading-none">{d.day}</span>
          </button>
        ))}
        <button
          type="button"
          onClick={() => setCustomOpen((v) => !v)}
          className={cn(
            "flex-1 flex flex-col items-center justify-center gap-0.5 py-3 rounded-xl border text-center transition-colors",
            !isQuickSelected && value
              ? "bg-[#A85C34] border-[#A85C34] text-white"
              : "bg-white border-[#EAEAEA] text-[#222222] hover:border-[#A85C34]"
          )}
          data-testid="checkout-date-other"
        >
          <CalendarPlus className="w-4 h-4" />
          <span className="text-[10px] font-medium">Diğer</span>
        </button>
      </div>
      {(customOpen || (!isQuickSelected && value)) && (
        <Input
          type="date"
          min={minDateISO}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-11 rounded-xl border-[#EAEAEA] focus-visible:ring-[#A85C34]"
          data-testid="checkout-date-custom"
        />
      )}
    </div>
  );
}

// Panelde tanımlı saat aralıklarını tıklanabilir kutucuklar olarak gösterir.
// Seçilen tarih bugünse ve aralığın başlangıcı geçmişse kutucuk listeden
// çıkarılmaz — sadece tema renginde soluk, tıklanamaz ve üstü çizili görünür,
// böylece müşteri o aralığın neden seçilemediğini görür.
export function SlotChipPicker({ slots, isToday, value, onChange }) {
  const nowHHMM = new Date().toTimeString().slice(0, 5);
  return (
    <div className="flex flex-wrap gap-2" data-testid="checkout-slot-picker">
      {slots.map((s) => {
        const key = `${s.start_time}-${s.end_time}`;
        const passed = isToday && s.start_time <= nowHHMM;
        const selected = value === key;
        return (
          <button
            key={key}
            type="button"
            disabled={passed}
            onClick={() => onChange(s.start_time, s.end_time)}
            className={cn(
              "px-3.5 py-2.5 rounded-xl border text-xs font-medium text-center transition-colors",
              passed
                ? "bg-[#A85C34]/5 border-[#A85C34]/15 text-[#A85C34]/35 line-through cursor-not-allowed"
                : selected
                ? "bg-[#A85C34] border-[#A85C34] text-white"
                : "bg-white border-[#EAEAEA] text-[#222222] hover:border-[#A85C34]"
            )}
            data-testid={`checkout-slot-option-${key}`}
          >
            {s.start_time}–{s.end_time}{s.label ? ` (${s.label})` : ""}
          </button>
        );
      })}
    </div>
  );
}
